import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for Membership (tiers, members, audit, usage ledger). Uses
// the shared axios instance (Bearer auth + 401 handling), following pickupBlockApi.js:
// reads return data or a safe default and toast on error; mutations return the record or
// null and let the caller toast success.
//
// Two conventions the API side enforces, worth knowing before calling anything here:
//
// 1. `merchantId` is REQUIRED on every read. A merchant-scoped admin has it inferred
//    from their own user, but a platform admin must name one — passing "all" or nothing
//    is a 400, not an unscoped list. That is deliberate: a tier carries a discount
//    percentage, so an unscoped list would be a cross-tenant leak of commercial terms.
//
// 2. There is NO delete, anywhere. A tier is retired with setTypeActive(id, false) and a
//    member is stopped with setMemberStatus(). Both rows stay: the usage ledger
//    attributes historical spend to a tier, and every past order points at one. The
//    foreign keys are RESTRICT, so the database refuses a delete even if a caller tried.

const readFail = (error, fallback, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return fallback;
};

const writeFail = (error, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return null;
};

/**
 * A save that lost a race.
 *
 * The edit forms submit every field on every save, so without the `revision` guard on the
 * API one admin's save silently reverts another's — including fields they never opened.
 * The API answers 409 STALE_REVISION instead, and hands back the row as it now stands.
 *
 * Returned as `{ conflict: true, current }` rather than null so the caller can offer a
 * reload instead of just reporting a failure. A caller that ignores the shape still sees a
 * truthy value it did not expect, which is why every caller here checks it explicitly.
 */
const CONFLICT_CODE = "STALE_REVISION";

const asConflict = (error) => {
  const body = error?.response?.data;
  if (error?.response?.status !== 409 || body?.errorCode !== CONFLICT_CODE) return null;
  errorToast(body.message);
  return { conflict: true, current: body.data ?? null };
};

// ── Membership types (tiers) ──────────────────────────────────────────────────────────

// GET /api/membership-types?merchantId=&isActive=&search= -> [{ ...type, activeMembers }]
export const getMembershipTypes = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/membership-types", { params });
    if (res?.data && !res.data.isError) return res.data.data || [];
    errorToast(res?.data?.message ?? "Error loading membership types");
    return [];
  } catch (error) {
    return readFail(error, [], "Error loading membership types");
  }
};

// GET /api/membership-types/:id -> the type with eligibleCategories / excludedProducts /
// eligibleBranches expanded, plus activeMembers.
export const getMembershipType = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/membership-types/${id}`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading membership type");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading membership type");
  }
};

export const createMembershipType = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/membership-types", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error creating membership type");
    return null;
  } catch (error) {
    return writeFail(error, "Error creating membership type");
  }
};

// An absent eligibility key means "leave alone"; an empty array means "clear it", which
// restores the open-by-default behaviour. Send the arrays only when the form owns them.
// Send `payload.revision` — the value the form loaded — and a 409 comes back as
// `{ conflict: true, current }` instead of the change silently overwriting somebody else's.
export const updateMembershipType = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/membership-types/${id}`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating membership type");
    return null;
  } catch (error) {
    return asConflict(error) || writeFail(error, "Error updating membership type");
  }
};

// Retire or restore a tier. This is the only "remove" there is — see the note at the top.
export const setMembershipTypeActive = async (id, isActive) => {
  try {
    const res = await axiosInstance.put(`/api/membership-types/${id}/active`, { isActive });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating tier status");
    return null;
  } catch (error) {
    return writeFail(error, "Error updating tier status");
  }
};

// ── Company groups ────────────────────────────────────────────────────────────────────
//
// A group is the organisation that bought the memberships: it carries a SHARED spend
// budget and names one member as the account handler.
//
// Three things to know before calling any of these:
//
// 1. A group is NOT a tier. The same company can have its manager on Leader and its staff
//    on Ambassador — a group answers "who oversees this and out of whose budget", a tier
//    answers "how much off". Never present them as one setting.
//
// 2. `Membership.groupId` is NULLABLE and null means an INDIVIDUAL member — which is the
//    majority case. Nobody has to create a one-person company to hand somebody a card.
//
// 3. Both budgets are optional, and `null` means NO limit — never zero. Zero would mean
//    "this company may never receive a discount", the opposite of a cleared field.
//
// And, as everywhere in this feature, there is no delete: retire a group with
// setMembershipGroupActive(id, false). The usage ledger attributes historical spend group
// by group and its foreign key is RESTRICT, so the database refuses a delete anyway.

// GET /api/membership-groups?merchantId=&isActive=&search=
// -> [{ ...group, members, activeMembers, spentThisMonth }]
export const getMembershipGroups = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/membership-groups", { params });
    if (res?.data && !res.data.isError) return res.data.data || [];
    errorToast(res?.data?.message ?? "Error loading groups");
    return [];
  } catch (error) {
    return readFail(error, [], "Error loading groups");
  }
};

// GET /api/membership-groups/:id -> the group with `members` expanded and a `usage` block
// carrying today's and this month's spend plus what is left of each budget.
export const getMembershipGroup = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/membership-groups/${id}`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading group");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading group");
  }
};

export const createMembershipGroup = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/membership-groups", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error creating group");
    return null;
  } catch (error) {
    return writeFail(error, "Error creating group");
  }
};

// Same concurrency guard as the tier update — send the loaded `revision`.
export const updateMembershipGroup = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/membership-groups/${id}`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating group");
    return null;
  } catch (error) {
    return asConflict(error) || writeFail(error, "Error updating group");
  }
};

// Retire or restore a group. This is the only "remove" there is — see the note above.
export const setMembershipGroupActive = async (id, isActive) => {
  try {
    const res = await axiosInstance.put(`/api/membership-groups/${id}/active`, { isActive });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating group status");
    return null;
  } catch (error) {
    return writeFail(error, "Error updating group status");
  }
};

/**
 * Appoint the account handler, or pass null to clear it.
 *
 * The nominee must ALREADY be a member of this group — the API refuses anyone else, and
 * says so, because the handler's view exposes every member's spend. Add them to the group
 * first (setMembershipOverrides with groupId), then appoint them.
 *
 * Only an admin can do this. There is deliberately no customer-facing route, or a member
 * could claim any company and read its figures.
 */
export const setMembershipGroupHandler = async (id, membershipId) => {
  try {
    const res = await axiosInstance.put(`/api/membership-groups/${id}/handler`, { membershipId });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error setting the handler");
    return null;
  } catch (error) {
    return writeFail(error, "Error setting the handler");
  }
};

// ── Members ───────────────────────────────────────────────────────────────────────────

// GET /api/memberships?merchantId=&status=&membershipTypeId=&expiringInDays=&search=&page=&limit=
// -> { rows, total, page, limit, totalPages }
// `search` is one box across card number, employee id, name, phone and email.
export const getMemberships = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/memberships", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading members");
    return { rows: [], total: 0, page: 1, limit: 25, totalPages: 0 };
  } catch (error) {
    return readFail(error, { rows: [], total: 0, page: 1, limit: 25, totalPages: 0 }, "Error loading members");
  }
};

export const getMembership = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/memberships/${id}`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading member");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading member");
  }
};

// POST /api/memberships — assign, or re-issue to someone who already has a row.
// One membership per (user, merchant) is a unique index, so this is an upsert in effect.
export const assignMembership = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/memberships", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error assigning membership");
    return null;
  } catch (error) {
    return writeFail(error, "Error assigning membership");
  }
};

// Many customers, one tier. Returns the whole report — `{ assignedCount, totalCount, results }`
// — because partial success is the normal outcome here: anyone who already holds a card is
// reported and left alone rather than moved onto the chosen tier. Callers must render the
// report; a bare success toast would hide the customers who were skipped.
export const bulkAssignMemberships = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/memberships/bulk", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error assigning memberships");
    return null;
  } catch (error) {
    return writeFail(error, "Error assigning memberships");
  }
};

// `reason` is internal, admin eyes only. `customerMessage` is what the pickup app shows
// the member. They are separate fields precisely so an internal note cannot reach the
// customer by being typed into the wrong box — keep the two inputs separate in the UI.
export const setMembershipStatus = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/memberships/${id}/status`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating member status");
    return null;
  } catch (error) {
    return writeFail(error, "Error updating member status");
  }
};

// Per-member caps, employee id, leader, photo and verification.
//
// This is the one edit that lifts a member off their tier's published terms, so the API
// writes a before/after audit row inside the same transaction — there is no way to change
// an override without leaving a record of what it replaced.
export const setMembershipOverrides = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/memberships/${id}/overrides`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating overrides");
    return null;
  } catch (error) {
    return writeFail(error, "Error updating overrides");
  }
};

// action: "disable" | "enable" | "reissue"
export const rotateMembershipQr = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/memberships/${id}/qr`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating QR");
    return null;
  } catch (error) {
    return writeFail(error, "Error updating QR");
  }
};

export const addMembershipNote = async (id, internalNote) => {
  try {
    const res = await axiosInstance.put(`/api/memberships/${id}/note`, { internalNote });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving note");
    return null;
  } catch (error) {
    return writeFail(error, "Error saving note");
  }
};

// GET /api/memberships/:id/audits?page=&limit=
// -> { rows, total, page, limit, totalPages }, newest first.
//
// Append-only, so it only ever grows — hence real pagination rather than one capped
// array. `total` is the whole history, not the page, so the count above the table does not
// change as you page. Rows carry previousValues/newValues as a diff of only the keys that
// actually moved.
export const getMembershipAudits = async (id, params = {}) => {
  const empty = { rows: [], total: 0, page: 1, limit: 20, totalPages: 0 };
  try {
    const res = await axiosInstance.get(`/api/memberships/${id}/audits`, { params });
    if (res?.data && !res.data.isError) return res.data.data || empty;
    errorToast(res?.data?.message ?? "Error loading history");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading history");
  }
};

// ── Usage ledger ──────────────────────────────────────────────────────────────────────

// GET /api/membership-usage?merchantId=&membershipId=&membershipTypeId=&eventType=&dateFrom=&dateTo=&page=&limit=
// -> { rows, total, page, limit, totalPages, totals: { discountGross, discountNet } }
//
// `totals` covers the whole filtered set, not the current page — the page number must not
// change what the summary says. Revoke rows are stored NEGATIVE, so the sum is already
// net of returns; do not take absolute values of it.
export const getMembershipUsage = async (params = {}) => {
  const empty = { rows: [], total: 0, page: 1, limit: 50, totalPages: 0, totals: { discountGross: 0, discountNet: 0 } };
  try {
    const res = await axiosInstance.get("/api/membership-usage", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading usage");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading usage");
  }
};

// GET /api/membership-scans?merchantId=&membershipId=&result=&branchId=&page=&limit=
// Every verification attempt at the counter, successes AND failures. The failures are the
// point: a rejected scan produces no order and no ledger row, so this is the only place
// "the cashier said my card did not work" can be answered from.
export const getMembershipScans = async (params = {}) => {
  const empty = { rows: [], total: 0, page: 1, limit: 50, totalPages: 0 };
  try {
    const res = await axiosInstance.get("/api/membership-scans", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading scan log");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading scan log");
  }
};

// ── Configuration history ─────────────────────────────────────────────────────────────

// GET /api/membership-config-audits?merchantId=&entityType=&entityId=&action=&page=&limit=
// -> { rows, total, page, limit, totalPages }, newest first.
//
// Tier and company edits, both from one table. `entityType` is mandatory whenever
// `entityId` is given — the id is not a foreign key, so tier 5 and company 5 both exist
// and an unqualified id would mix their two histories. The API refuses that with a 400
// rather than returning the mixture.
//
// Rows carry previousValues/newValues as a diff of only the keys that moved, because the
// edit forms submit every field on every save.
export const getMembershipConfigAudits = async (params = {}) => {
  const empty = { rows: [], total: 0, page: 1, limit: 20, totalPages: 0 };
  try {
    const res = await axiosInstance.get("/api/membership-config-audits", { params });
    if (res?.data && !res.data.isError) return res.data.data || empty;
    errorToast(res?.data?.message ?? "Error loading configuration history");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading configuration history");
  }
};

// ── Analytics ─────────────────────────────────────────────────────────────────────────

// GET /api/reporting/membership?merchantId=&days= — the aggregate view.
//
// Lives here rather than in apiCalls.js with the other reporting helpers so everything
// membership calls from one module. Every money figure it returns is already NET OF
// RETURNS (summed from the signed usage ledger), so never take an absolute value of one.
export const getMembershipAnalytics = async (merchantId, { days } = {}) => {
  try {
    const res = await axiosInstance.get("/api/reporting/membership", {
      params: { merchantId, days: days || undefined },
    });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading membership analytics");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading membership analytics");
  }
};
